/** Lux & billing › Details: every lot of Lux the company bought or was given (backend GET /v1/orgs/{id}/lux/lots;
 * docs/features/lux.md § Expiry). On top, the summary: Lux available in lots and the next to expire, with the next
 * three expirations; below, one row per lot: acquired, how, price paid, Lux, spent, left, expires. Lots are spent
 * soonest-expiring first, so the list's order is the spending order. Integers in, formatted strings out. */
import { CalendarClock, Layers } from "lucide-react";
import type { S } from "../api/client";
import { useLuxLots } from "../api/queries";
import { LuxGlyph } from "../components/LuxIcon";
import { Alert, Badge, Card, Empty, Spinner, cx } from "../components/ui";
import { useLuxFormat } from "../components/LuxSpend";
import type { MessageKey } from "../i18n/keys";
import { isSoon, lotLabel, untilText } from "../lib/luxLots";

type Lot = S["LuxLot"];
type Summary = S["LuxLotsSummary"];

const STATUS: Record<Lot["status"], { label: MessageKey; tone?: "ok" | "warn" | "danger" | "info" | "grad" }> = {
  active: { label: "lux.details.status.active", tone: "ok" },
  used: { label: "lux.details.status.used" },
  expired: { label: "lux.details.status.expired", tone: "danger" },
};

/** The pointer in the wallet card: purchased Lux expires, see Details. */
export function ExpiryPointer({ summary }: { summary: Summary }) {
  const f = useLuxFormat();
  const next = summary.next;
  return (
    <span>
      {next ? f.t("lux.details.pointerNext", { amount: f.lux(next.amount), date: f.date(next.expires_at) }) : f.t("lux.details.pointerNone")} ·{" "}
      <a href="#details" className="lux-details-link">
        {f.t("lux.details.title")}
      </a>
    </span>
  );
}

function SummaryStrip({ summary }: { summary: Summary }) {
  const f = useLuxFormat();
  const { t } = f;
  const next = summary.next;
  const soon = next != null && isSoon(next.expires_at);
  return (
    <div className="lux-lots-summary">
      <div className="lux-lots-tile">
        <span className="label">
          <LuxGlyph size={15} /> {t("lux.details.available")}
        </span>
        <div className="lux-lots-big">
          <span className="i18n-ltr">{f.lux(summary.available)}</span> <small>Lux</small>
        </div>
        <span className="muted small">{summary.lots_open === 0 ? t("lux.details.noLotLeft") : t("lux.details.inLots", { count: summary.lots_open })}</span>
      </div>
      <div className={cx("lux-lots-tile", soon && "is-soon")}>
        <span className="label">
          <LuxGlyph size={15} /> {t("lux.details.next")}
        </span>
        {next ? (
          <>
            <div className="lux-lots-big">
              <span className="i18n-ltr">{f.lux(next.amount)}</span> <small>Lux</small>
            </div>
            <span className={cx("small", soon ? "lux-soon-text" : "muted")}>
              {t("lux.details.nextOn", { date: f.date(next.expires_at), until: untilText(next.expires_at, Date.now(), f) })}
            </span>
          </>
        ) : (
          <>
            <div className="lux-lots-big is-none">{t("lux.details.nothing")}</div>
            <span className="muted small">{t("lux.details.nothingWaiting")}</span>
          </>
        )}
      </div>
      <div className="lux-lots-tile lux-lots-upcoming">
        <span className="label">
          <CalendarClock size={14} /> {t("lux.details.comingUp")}
        </span>
        {summary.upcoming.length === 0 ? (
          <span className="muted small">{t("lux.details.comingUpNone")}</span>
        ) : (
          <ol>
            {summary.upcoming.map((u) => (
              <li key={u.lot_id} className={cx(isSoon(u.expires_at) && "is-soon")}>
                <span className="lux-up-date">{f.date(u.expires_at)}</span>
                <span className="lux-up-kind">{t("lux.lot.kind", { kind: u.kind })}</span>
                <b className="lux-up-amount i18n-ltr">{f.lux(u.amount)}</b>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function LotRow({ lot }: { lot: Lot }) {
  const f = useLuxFormat();
  const { t } = f;
  const label = lotLabel(lot, f);
  const soon = lot.status === "active" && isSoon(lot.expires_at);
  return (
    <tr className={cx("lux-lot", lot.status !== "active" && "is-done")}>
      <td className="lux-lot-when nowrap">
        <span className="lux-m">{t("lux.details.m.acquired")} </span>
        {f.date(lot.acquired_at)}
      </td>
      <td className="lux-lot-how">
        <b>{label.title}</b>
        {label.detail && <span className="muted small"> · {label.detail}</span>}
      </td>
      <td className="lux-lot-paid num nowrap">
        <span className="lux-m">{t("lux.details.m.paid")} </span>
        {lot.cost_minor > 0 ? f.money(lot.cost_minor, lot.currency) : <span className="faint">{t("lux.details.noCharge")}</span>}
      </td>
      <td className="lux-lot-lux num nowrap">
        {f.lux(lot.lux)}
        <span className="lux-m"> {t("lux.details.m.acquiredLux")}</span>
      </td>
      <td className="lux-lot-spent num nowrap">
        {f.lux(lot.spent)}
        <span className="lux-m"> {t("lux.details.m.spent")}</span>
        {lot.expired > 0 && <div className="lux-lot-expired small">{t("lux.details.expiredAmount", { amount: f.lux(lot.expired) })}</div>}
      </td>
      <td className="lux-lot-left num nowrap">
        <b>{f.lux(lot.remaining)}</b>
        <span className="lux-m"> {t("lux.details.m.left")}</span>
      </td>
      <td className="lux-lot-expires nowrap">
        <span className={cx(soon && "lux-soon-text")}>{f.date(lot.expires_at)}</span>
        {lot.status === "active" ? (
          <div className={cx("small", soon ? "lux-soon-text" : "faint")}>{untilText(lot.expires_at, Date.now(), f)}</div>
        ) : (
          <div className="small">
            <Badge tone={STATUS[lot.status].tone}>{t(STATUS[lot.status].label)}</Badge>
          </div>
        )}
      </td>
    </tr>
  );
}

/** The Details section (anchor `#details`). */
export function LuxDetails({ orgId }: { orgId: string }) {
  const lots = useLuxLots(orgId);
  const { t } = useLuxFormat();
  return (
    <section id="details" className="lux-details">
      <Card
        title={
          <span className="row" style={{ gap: 8 }}>
            <CalendarClock size={18} /> {t("lux.details.title")}
          </span>
        }
        description={t("lux.details.description")}
        pad={false}
      >
        {lots.isLoading ? (
          <Spinner />
        ) : lots.error || !lots.data ? (
          <div style={{ padding: 16 }}>
            <Alert tone="danger">{t("lux.details.loadError")}</Alert>
          </div>
        ) : (
          <>
            <SummaryStrip summary={lots.data.summary} />
            {lots.data.items.length === 0 ? (
              <Empty icon={<Layers />} title={t("lux.details.emptyTitle")}>
                {t("lux.details.emptyBody")}
              </Empty>
            ) : (
              <div className="table-wrap">
                <table className="table lux-lots">
                  <thead>
                    <tr>
                      <th>{t("lux.details.th.acquired")}</th>
                      <th>{t("lux.details.th.how")}</th>
                      <th className="num">{t("lux.details.th.paid")}</th>
                      <th className="num">Lux</th>
                      <th className="num">{t("lux.details.th.spent")}</th>
                      <th className="num">{t("lux.details.th.left")}</th>
                      <th>{t("lux.details.th.expires")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lots.data.items.map((lot) => (
                      <LotRow key={lot.id} lot={lot} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="lux-lots-foot muted small">
              {t("lux.details.foot")}
            </p>
          </>
        )}
      </Card>
    </section>
  );
}
